import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listBackupDays,
  updateBackupDays,
  type ListBackupDaysParams,
} from '@/api/backupDaysMatrix'
import type { BackupDaysMode, BackupDaysRow } from '@/api/clt.types'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

const KEY = ['backup-days'] as const

export function useBackupDays(params: ListBackupDaysParams = {}) {
  return useQuery({
    queryKey: [...KEY, params],
    queryFn: () => listBackupDays(params),
  })
}

export function useUpdateBackupDays() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      origin,
      wh,
      mode,
      days,
    }: {
      origin: string
      wh: number
      mode: BackupDaysMode
      days: number
    }) => updateBackupDays(origin, wh, mode, days),
    onMutate: async ({ origin, wh, mode, days }) => {
      // Optimistic update so the inline input doesn't flicker back to the old
      // value while the round-trip is in flight.
      await queryClient.cancelQueries({ queryKey: KEY })
      const previous = queryClient.getQueriesData({ queryKey: KEY })
      queryClient.setQueriesData(
        { queryKey: KEY },
        (data: { data: BackupDaysRow[]; pagination: unknown } | undefined) => {
          if (!data) return data
          return {
            ...data,
            data: data.data.map(row =>
              row.origin === origin && row.wh === wh
                ? { ...row, [mode]: days }
                : row
            ),
          }
        }
      )
      return { previous }
    },
    onError: (error: unknown, _vars, context) => {
      if (context?.previous) {
        for (const [key, value] of context.previous) {
          queryClient.setQueryData(key, value)
        }
      }
      toast.error(getErrorMessage(error, 'Failed to update backup days'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: KEY })
    },
  })
}
